/**
 * NEWSクラス
 * @constructor
 */
class News {
  constructor() {
    // TOPページの表示件数制限
    this.topLimit = 5;

    // 各要素のセレクタ
    this.selectors = {
      topKv: '.p-topKv__news',          // TOP: KV小窓（最新1件）
      topList: '.p-top-news__list',      // TOP: 最新ニュース一覧（3件制限）
      pageContainer: '.p-news__container', // 下層: 年別セクション群を挿入する親要素
    };

    this.init();
  }

  async init() {
    const topKvContainer = document.querySelector(this.selectors.topKv);
    const topListContainer = document.querySelector(this.selectors.topList);
    const pageContainer = document.querySelector(this.selectors.pageContainer);

    // どの要素も存在しないページでは処理をスキップ
    if (!topKvContainer && !topListContainer && !pageContainer) return;

    try {
      // キャッシュ対策をして public/news.json を取得
      const response = await fetch(`/news.json?v=${Date.now()}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const newsList = await response.json();
      if (!newsList || newsList.length === 0) return;

      // 1. TOPページ: KV小窓（最新1件）の描画
      if (topKvContainer) {
        this.renderTopKvNews(topKvContainer, newsList[0]);
      }

      // 2. TOPページ: ニュース一覧（最新N件）の描画
      if (topListContainer) {
        const displayNews = newsList.slice(0, this.topLimit);
        topListContainer.innerHTML = this.renderTopNewsList(displayNews);
      }

      // 3. 下層ページ: 年ごとにグループ化してセクション一覧を描画
      if (pageContainer) {
        const groupedNews = this.groupByYear(newsList);
        pageContainer.innerHTML = this.renderPageNewsSections(groupedNews);
      }
    } catch (error) {
      console.error('Failed to load news.json:', error);
    }
  }

  /**
   * "YYYY-MM-DD" を "YYYY年M月D日" フォーマットに変換
   */
  formatDate(dateStr) {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return dateStr;
    return `${year}年${parseInt(month, 10)}月${parseInt(day, 10)}日`;
  }

  /**
   * 投稿日から指定日数以内（デフォルト30日以内）か判定してNEWラベルを表示
   */
  isNewPost(dateStr, daysThreshold = 30) {
    if (!dateStr) return false;
    const postDate = new Date(dateStr).getTime();
    const today = Date.now();
    const diffDays = (today - postDate) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= daysThreshold;
  }

  /**
   * 配列データを「年（YYYY）」ごとにグループ化する関数
   */
  groupByYear(items) {
    const groups = {};

    items.forEach((item) => {
      // "2026-09-28" から "2026" を抽出
      const year = item.date ? item.date.split('-')[0] : 'その他';
      if (!groups[year]) {
        groups[year] = [];
      }
      groups[year].push(item);
    });

    // 年の降順（新しい年が上）にソートして配列化
    return Object.keys(groups)
      .sort((a, b) => b - a)
      .map((year) => ({
        year,
        items: groups[year],
      }));
  }

  /**
   * 【TOP用】KV内の小窓エリア（最新1件）
   */
  renderTopKvNews(container, latestNews) {
    const isPdf = latestNews.link_type === 'pdf' || (latestNews.url && latestNews.url.endsWith('.pdf'));
    const formattedDate = this.formatDate(latestNews.date);
    const targetAttr = isPdf ? ' target="_blank" rel="noopener noreferrer"' : '';

    container.innerHTML = `
      <span class="p-topKv__news-label">NEWS</span>
      <time class="p-topKv__news-date">${formattedDate}</time>
      <a href="${latestNews.url}"${targetAttr} class="p-topKv__news-link">
        <span class="p-topKv__news-text">${latestNews.title}</span>
      </a>
    `;
  }

  /**
   * 【TOP用】ニュース一覧（最新3件）
   */
  renderTopNewsList(items) {
    return items.map((item) => {
      const isPdf = item.link_type === 'pdf' || (item.url && item.url.endsWith('.pdf'));
      const isNew = this.isNewPost(item.date);
      const formattedDate = this.formatDate(item.date);
      const targetAttr = isPdf ? ' target="_blank" rel="noopener noreferrer"' : '';

      const newLabelHtml = isNew ? '<span class="p-top-news__label">NEW</span>' : '';
      const pdfIconHtml = isPdf ? `
        <span class="p-top-news__pdf" aria-label="PDFを開く">
          <img src="/assets/img/common/pdf.svg" alt="PDF">
        </span>
      ` : '';

      return `
        <li>
          <a class="p-top-news__item" href="${item.url}"${targetAttr}>
            <span class="p-top-news__date">${formattedDate}</span>
            ${newLabelHtml}
            <span class="p-top-news__text">
              ${item.title}
              ${pdfIconHtml}
            </span>
          </a>
        </li>
      `;
    }).join('');
  }

  /**
   * 【下層ページ用】年ごとの <section class="p-news__year-section"> 群をレンダリング
   */
  renderPageNewsSections(groups) {
    return groups.map((group) => {
      const listItemsHtml = group.items.map((item) => {
        const formattedDate = this.formatDate(item.date);
        const isPdf = item.link_type === 'pdf' || (item.url && item.url.endsWith('.pdf'));
        const keyAttr = `${formattedDate}${item.title}`;

        const pdfIconHtml = isPdf ? `
          <span class="p-news__pdf" aria-label="PDFを開く">
            <img src="/assets/img/common/pdf.svg" alt="PDF">
          </span>
        ` : '';

        // URLが存在する場合は <a> タグ、無い場合は <div> (disabled) で出力
        const innerContentHtml = item.url ? `
          <a
            class="p-news__link"
            href="${item.url}"
            ${isPdf ? 'target="_blank" rel="noopener noreferrer"' : ''}
          >
            <div class="p-news__date">${formattedDate}</div>
            <p class="p-news__text">${item.title}</p>
            ${pdfIconHtml}
          </a>
        ` : `
          <div class="p-news__link p-news__link--disabled">
            <div class="p-news__date">${formattedDate}</div>
            <p class="p-news__text">${item.title}</p>
            ${pdfIconHtml}
          </div>
        `;

        return `
          <li class="p-news__item" key="${keyAttr}">
            ${innerContentHtml}
          </li>
        `;
      }).join('');

      return `
        <section class="p-news__year-section" key="${group.year}">
          <h2 class="p-news__year">${group.year}年</h2>
          <ul class="p-news__list">
            ${listItemsHtml}
          </ul>
        </section>
      `;
    }).join('');
  }
}

export default News;